export type PainFactSource = 'organisation' | 'stream-candidate' | 'missing-source';
export type PainFactStatus = 'confirmed' | 'suggestion' | 'missing-source';
export type PainLinkTargetKind = 'step' | 'wait' | 'bottleneck' | 'outcome';

export interface PainFact {
  id: string;
  source: PainFactSource;
  sourceId: string;
  status: PainFactStatus;
  title: string;
  detail?: string;
  whoFeelsIt?: string;
  evidence?: string[];
  candidateCount?: number;
  confidence?: 'low' | 'medium';
}

export interface PainLinkTarget {
  kind: PainLinkTargetKind;
  id: string;
  label: string;
  statementId?: number;
  processId?: string;
}

export interface StreamPainLink {
  id: string;
  painFactId: string;
  painSource: PainFactSource;
  painSourceId: string;
  painTitle: string;
  target: PainLinkTarget;
}

export interface LinkedPainEvidence {
  target: PainLinkTarget;
  pain: PainFact[];
}

export interface StreamPainFactsReadModel {
  facts: PainFact[];
  confirmedFacts: PainFact[];
  candidates: PainFact[];
  links: StreamPainLink[];
  unresolved: PainFact[];
  targetOptions: PainLinkTarget[];
  linkedEvidence: LinkedPainEvidence[];
}

export interface PainFactsMessageLike {
  type?: string;
  text?: string;
}

export interface PainFactsSessionLike {
  conversationHistory?: PainFactsMessageLike[];
  spine?: Array<{ order?: number; label?: string }>;
  newModel?: {
    flowText?: string;
    vsm?: {
      processes?: Array<{
        id?: string;
        label?: string;
        steps?: Array<{ statementId?: number; wt?: string | number | null }>;
      }>;
    };
  };
}

export interface StreamPainFactsInput {
  organisation?: unknown;
  stream: {
    id: string;
    name: string;
    painLinks?: unknown;
    painCandidates?: unknown;
    streamPainCandidates?: unknown;
  };
  session?: PainFactsSessionLike | null;
  sharedSteps?: Array<{ stepId: string; processId: string; label: string }>;
}

interface OrganisationPainPointLike {
  id?: string;
  title?: string;
  detail?: string;
  whoFeelsIt?: string;
}

const PAIN_CUE =
  /\b(pain|problem|issue|slow|delay|delayed|waiting|waits?|stuck|blocked|manual|rework|frustrat|bottleneck|not working|doesn't work|cannot|can't|hard to|waste|duplicate|chase|missing)\b/i;

export function buildStreamPainFactsModel(input: StreamPainFactsInput): StreamPainFactsReadModel {
  const confirmedFacts = coerceOrganisationPainFacts(input.organisation);
  const candidates = mergeCandidates([
    ...coerceStreamCandidates(input.stream),
    ...derivePainCandidatesFromConversation(input.session?.conversationHistory ?? []),
  ]);
  const links = coercePainLinks(input.stream.painLinks).map(link => {
    const step = input.sharedSteps?.find(s => s.stepId === link.target.id);
    return step ? { ...link, target: { ...link.target, label: step.label, processId: step.processId } } : link;
  });
  const factsById = new Map<string, PainFact>();
  for (const fact of [...confirmedFacts, ...candidates]) factsById.set(fact.id, fact);

  const missingFacts = links
    .filter((link) => !factsById.has(link.painFactId))
    .map((link): PainFact => ({
      id: link.painFactId,
      source: 'missing-source',
      sourceId: link.painSourceId,
      status: 'missing-source',
      title: link.painTitle || 'Missing pain source',
      detail: 'The original pain point is no longer in the organisation context.',
    }));

  const facts = [...confirmedFacts, ...candidates, ...missingFacts];

  return {
    facts,
    confirmedFacts,
    candidates,
    links,
    unresolved: deriveUnresolvedPainFacts(facts, links),
    targetOptions: input.sharedSteps ? [
      ...input.sharedSteps.map(step => ({ kind: 'step' as const, id: step.stepId, label: step.label, processId: step.processId })),
      { kind: 'outcome', id: `outcome:${input.stream.id}`, label: `${input.stream.name} outcome` },
    ] : derivePainTargetOptions(input.stream, input.session ?? null),
    linkedEvidence: deriveLinkedPainEvidence(facts, links),
  };
}

export function deriveUnresolvedPainFacts(
  facts: PainFact[],
  links: StreamPainLink[],
): PainFact[] {
  const linkedFactIds = new Set(links.map((link) => link.painFactId));
  return facts.filter((fact) => fact.status !== 'missing-source' && !linkedFactIds.has(fact.id));
}

export function deriveLinkedPainEvidence(
  facts: PainFact[],
  links: StreamPainLink[],
): LinkedPainEvidence[] {
  const factsById = new Map(facts.map((fact) => [fact.id, fact]));
  const grouped = new Map<string, LinkedPainEvidence>();

  for (const link of links) {
    const fact = factsById.get(link.painFactId) ?? missingFactFromLink(link);
    const key = targetKey(link.target);
    const existing = grouped.get(key);
    if (existing) {
      existing.pain.push(fact);
    } else {
      grouped.set(key, { target: link.target, pain: [fact] });
    }
  }

  return Array.from(grouped.values());
}

export function derivePainTargetOptions(
  stream: { id: string; name: string },
  session?: PainFactsSessionLike | null,
): PainLinkTarget[] {
  const targets: PainLinkTarget[] = [];
  const seen = new Set<string>();

  const addTarget = (target: PainLinkTarget) => {
    const key = targetKey(target);
    if (seen.has(key)) return;
    seen.add(key);
    targets.push(target);
  };

  for (const step of session?.spine ?? []) {
    const order = Number(step.order);
    const label = cleanText(step.label);
    if (!Number.isFinite(order) || order <= 0 || !label) continue;
    addTarget({
      kind: 'step',
      id: `step:${order}`,
      label: `Step ${order}: ${label}`,
      statementId: order,
    });
  }

  for (const target of deriveFlowStepTargets(session?.newModel?.flowText ?? '')) {
    addTarget(target);
  }

  for (const process of session?.newModel?.vsm?.processes ?? []) {
    const processId = cleanText(process.id) || 'vsm-process';
    const processLabel = cleanText(process.label);
    for (const step of process.steps ?? []) {
      const statementId = Number(step.statementId);
      if (!Number.isFinite(statementId) || statementId <= 0 || !hasWait(step.wt)) continue;
      const suffix = processLabel ? ` in ${processLabel}` : '';
      addTarget({
        kind: 'wait',
        id: `wait:${processId}:${statementId}`,
        label: `Wait after step ${statementId}${suffix}`,
        statementId,
        processId,
      });
      addTarget({
        kind: 'bottleneck',
        id: `bottleneck:${processId}:${statementId}`,
        label: `Bottleneck candidate at step ${statementId}${suffix}`,
        statementId,
        processId,
      });
    }
  }

  addTarget({
    kind: 'outcome',
    id: `outcome:${stream.id}`,
    label: `${stream.name} outcome`,
  });

  return targets;
}

export function linkPainToTarget(
  existingLinks: StreamPainLink[],
  pain: PainFact,
  target: PainLinkTarget,
): StreamPainLink[] {
  const links = coercePainLinks(existingLinks);
  if (
    links.some(
      (link) =>
        link.painFactId === pain.id &&
        link.target.kind === target.kind &&
        link.target.id === target.id,
    )
  ) {
    return links;
  }

  return [
    ...links,
    {
      id: makeLinkId(pain.id, target),
      painFactId: pain.id,
      painSource: pain.source,
      painSourceId: pain.sourceId,
      painTitle: pain.title,
      target,
    },
  ];
}

export function unlinkPainFromTarget(
  existingLinks: StreamPainLink[],
  painFactId: string,
  target: PainLinkTarget,
): StreamPainLink[] {
  return coercePainLinks(existingLinks).filter(
    (link) =>
      !(
        link.painFactId === painFactId &&
        link.target.kind === target.kind &&
        link.target.id === target.id
      ),
  );
}

export function unlinkPainLink(existingLinks: StreamPainLink[], linkId: string): StreamPainLink[] {
  return coercePainLinks(existingLinks).filter((link) => link.id !== linkId);
}

export function replaceStreamPainLinks(
  streamsSubDoc: unknown,
  streamId: string,
  links: StreamPainLink[],
): { streams: unknown[] } {
  const record =
    streamsSubDoc && typeof streamsSubDoc === 'object'
      ? (streamsSubDoc as Record<string, unknown>)
      : {};
  const streams = Array.isArray(record.streams) ? record.streams : [];
  const nextLinks = coercePainLinks(links);
  let replaced = false;

  const nextStreams = streams.map((candidate) => {
    if (!candidate || typeof candidate !== 'object') return candidate;
    const stream = candidate as Record<string, unknown>;
    if (stream.id !== streamId) return candidate;
    replaced = true;
    return { ...stream, painLinks: nextLinks };
  });

  if (!replaced) {
    nextStreams.push({ id: streamId, painLinks: nextLinks });
  }

  return { ...record, streams: nextStreams };
}

export function coercePainLinks(raw: unknown): StreamPainLink[] {
  if (!Array.isArray(raw)) return [];
  const links: StreamPainLink[] = [];

  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const target = coerceTarget(record.target);
    const painFactId = cleanText(record.painFactId) || cleanText(record.painPointId);
    const painTitle = cleanText(record.painTitle) || cleanText(record.title);
    if (!target || !painFactId) continue;
    const painSource = coerceSource(record.painSource) ?? inferSourceFromFactId(painFactId);
    const painSourceId =
      cleanText(record.painSourceId) || painFactId.replace(/^(org|candidate|missing):/, '');
    links.push({
      id: cleanText(record.id) || makeLinkId(painFactId, target),
      painFactId,
      painSource,
      painSourceId,
      painTitle: painTitle || painFactId,
      target,
    });
  }

  return links;
}

function coerceOrganisationPainFacts(organisation: unknown): PainFact[] {
  if (!organisation || typeof organisation !== 'object') return [];
  const painPoints = (organisation as { painPoints?: unknown }).painPoints;
  if (!Array.isArray(painPoints)) return [];

  return painPoints
    .map((point: OrganisationPainPointLike, index): PainFact | null => {
      const title = cleanText(point?.title);
      if (!title) return null;
      const sourceId = cleanText(point.id) || slugify(title) || `pain-${index + 1}`;
      return {
        id: `org:${sourceId}`,
        source: 'organisation',
        sourceId,
        status: 'confirmed',
        title,
        detail: cleanText(point.detail) || undefined,
        whoFeelsIt: cleanText(point.whoFeelsIt) || undefined,
      };
    })
    .filter((fact): fact is PainFact => fact !== null);
}

function coerceStreamCandidates(stream: {
  painCandidates?: unknown;
  streamPainCandidates?: unknown;
}): PainFact[] {
  const raw = Array.isArray(stream.streamPainCandidates)
    ? stream.streamPainCandidates
    : stream.painCandidates;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item, index): PainFact | null => {
      const record = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
      const title = cleanText(record.title) || cleanText(record.text) || cleanText(item);
      if (!title) return null;
      const sourceId = cleanText(record.id) || slugify(title) || `candidate-${index + 1}`;
      const evidence = coerceEvidence(record.evidence, title);
      return {
        id: `candidate:${sourceId}`,
        source: 'stream-candidate',
        sourceId,
        status: 'suggestion',
        title,
        evidence,
        candidateCount: evidence.length,
        confidence: coerceConfidence(record.confidence),
      };
    })
    .filter((fact): fact is PainFact => fact !== null);
}

function derivePainCandidatesFromConversation(messages: PainFactsMessageLike[]): PainFact[] {
  const grouped = new Map<
    string,
    { title: string; evidence: string[]; firstIndex: number }
  >();

  messages.forEach((message, messageIndex) => {
    if (message.type === 'agent') return;
    for (const sentence of splitSentences(message.text ?? '')) {
      if (!PAIN_CUE.test(sentence)) continue;
      const title = cleanCandidateTitle(sentence);
      if (!title) continue;
      const key = normalizeCandidate(title);
      const existing = grouped.get(key);
      if (existing) {
        existing.evidence.push(title);
      } else {
        grouped.set(key, { title, evidence: [title], firstIndex: messageIndex });
      }
    }
  });

  return Array.from(grouped.values())
    .sort((a, b) => a.firstIndex - b.firstIndex)
    .map((candidate) => ({
      id: `candidate:${slugify(candidate.title)}`,
      source: 'stream-candidate',
      sourceId: slugify(candidate.title),
      status: 'suggestion',
      title: candidate.title,
      evidence: candidate.evidence,
      candidateCount: candidate.evidence.length,
      confidence: 'low',
    }));
}

function mergeCandidates(candidates: PainFact[]): PainFact[] {
  const grouped = new Map<string, PainFact>();

  for (const candidate of candidates) {
    const key = normalizeCandidate(candidate.title);
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, candidate);
      continue;
    }
    const evidence = [...(existing.evidence ?? []), ...(candidate.evidence ?? [])];
    grouped.set(key, {
      ...existing,
      evidence,
      candidateCount: evidence.length || (existing.candidateCount ?? 1) + (candidate.candidateCount ?? 1),
    });
  }

  return Array.from(grouped.values());
}

function deriveFlowStepTargets(flowText: string): PainLinkTarget[] {
  const targets: PainLinkTarget[] = [];
  for (const line of flowText.split(/\r?\n/)) {
    const match = line.match(/^\s*\[(\d+)]\s*(.+)$/);
    if (!match) continue;
    const statementId = Number(match[1]);
    const label = labelFromFlowLine(match[2]);
    if (!Number.isFinite(statementId) || !label) continue;
    targets.push({
      kind: 'step',
      id: `step:${statementId}`,
      label: `Step ${statementId}: ${label}`,
      statementId,
    });
  }
  return targets;
}

function labelFromFlowLine(line: string): string {
  const actionMatch = line.match(/\((?:action|command|query|event|reaction)\)\s*([^>]+)/i);
  return cleanText(actionMatch?.[1] ?? line)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ');
}

function coerceTarget(raw: unknown): PainLinkTarget | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const kind = record.kind;
  if (kind !== 'step' && kind !== 'wait' && kind !== 'bottleneck' && kind !== 'outcome') {
    return null;
  }
  const id = cleanText(record.id);
  const label = cleanText(record.label);
  if (!id || !label) return null;
  const statementId = Number(record.statementId);
  return {
    kind,
    id,
    label,
    statementId: Number.isFinite(statementId) ? statementId : undefined,
    processId: cleanText(record.processId) || undefined,
  };
}

function coerceSource(raw: unknown): PainFactSource | null {
  if (raw === 'organisation' || raw === 'stream-candidate' || raw === 'missing-source') {
    return raw;
  }
  return null;
}

function coerceConfidence(raw: unknown): 'low' | 'medium' {
  return raw === 'medium' ? 'medium' : 'low';
}

function inferSourceFromFactId(id: string): PainFactSource {
  if (id.startsWith('candidate:')) return 'stream-candidate';
  if (id.startsWith('missing:')) return 'missing-source';
  return 'organisation';
}

function coerceEvidence(raw: unknown, fallback: string): string[] {
  if (Array.isArray(raw)) {
    const evidence = raw.map((item) => cleanText(item)).filter(Boolean);
    if (evidence.length > 0) return evidence;
  }
  return [fallback];
}

function missingFactFromLink(link: StreamPainLink): PainFact {
  return {
    id: link.painFactId,
    source: 'missing-source',
    sourceId: link.painSourceId,
    status: 'missing-source',
    title: link.painTitle || 'Missing pain source',
  };
}

function splitSentences(text: string): string[] {
  return text
    .split(/[\n.!?]+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function cleanCandidateTitle(sentence: string): string {
  return cleanText(sentence)
    .replace(/^[-*]\s*/, '')
    .replace(/^pain(?: point)?:\s*/i, '')
    .replace(/\s+/g, ' ');
}

function normalizeCandidate(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function hasWait(value: string | number | null | undefined): boolean {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0;
  const text = cleanText(value).toLowerCase();
  if (!text || text === '0' || text === 'none' || text === 'n/a' || text === 'not captured') {
    return false;
  }
  const match = text.match(/^(\d+(?:\.\d+)?)\s*(s|m|h|d)?$/);
  if (!match) return true;
  return Number(match[1]) > 0;
}

function makeLinkId(painFactId: string, target: PainLinkTarget): string {
  return `pain-link:${painFactId}:${target.kind}:${target.id}`;
}

function targetKey(target: PainLinkTarget): string {
  return `${target.kind}:${target.id}`;
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
